import React from 'react';
import { Container, Typography, Box, Paper } from '@mui/material';
import { motion } from 'framer-motion';

const TermsOfService: React.FC = () => {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      style={{ minHeight: '100vh', backgroundColor: '#0a0e27', paddingTop: '2rem', paddingBottom: '4rem' }}
    >
      <Container maxWidth="md">
        <Paper elevation={3} sx={{ p: 4, backgroundColor: '#14192d' }}>
          <Typography variant="h3" gutterBottom sx={{ color: '#0066FF', fontWeight: 'bold', mb: 3 }}>
            Terms of Service
          </Typography>
          
          <Typography variant="body2" sx={{ color: '#888', mb: 4 }}>
            Last Updated: {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
          </Typography>

          <Box sx={{ '& > *': { mb: 3 } }}>
            <Box>
              <Typography variant="h5" gutterBottom sx={{ color: '#fff', fontWeight: 'bold', mb: 2 }}>
                1. Acceptance of Terms
              </Typography>
              <Typography variant="body1" sx={{ color: '#ccc', lineHeight: 1.8 }}>
                By accessing or using Reachout AI Corporation's services, you agree to be bound by these Terms of Service. If you do not agree to these terms, please do not use our services.
              </Typography>
            </Box>

            <Box>
              <Typography variant="h5" gutterBottom sx={{ color: '#fff', fontWeight: 'bold', mb: 2 }}>
                2. Description of Service
              </Typography>
              <Typography variant="body1" sx={{ color: '#ccc', lineHeight: 1.8 }}>
                Reachout AI provides an AI-powered customer engagement platform that enables businesses to manage customer communications, appointments, queues, and other business operations through automated and intelligent systems.
              </Typography>
            </Box>

            <Box>
              <Typography variant="h5" gutterBottom sx={{ color: '#fff', fontWeight: 'bold', mb: 2 }}>
                3. User Accounts
              </Typography>
              <Typography variant="body1" sx={{ color: '#ccc', lineHeight: 1.8, mb: 2 }}>
                To use our services, you must:
              </Typography>
              <ul style={{ color: '#ccc', lineHeight: 1.8, marginLeft: '1.5rem' }}>
                <li>Provide accurate and complete registration information</li>
                <li>Maintain the security of your account credentials</li>
                <li>Be at least 18 years of age</li>
                <li>Notify us immediately of any unauthorized use of your account</li>
                <li>Be responsible for all activities that occur under your account</li>
              </ul>
            </Box>

            <Box>
              <Typography variant="h5" gutterBottom sx={{ color: '#fff', fontWeight: 'bold', mb: 2 }}>
                4. SMS Terms
              </Typography>
              <Typography variant="body1" sx={{ color: '#ccc', lineHeight: 1.8, mb: 2 }}>
                Our platform provides SMS messaging capabilities for business communications. By using our SMS services:
              </Typography>
              <Box sx={{ 
                backgroundColor: 'rgba(0, 102, 255, 0.1)', 
                padding: '1.5rem', 
                borderRadius: '8px',
                border: '1px solid rgba(0, 102, 255, 0.3)',
                mb: 2
              }}>
                <Typography variant="body1" sx={{ color: '#fff', lineHeight: 1.8, mb: 1.5 }}>
                  <strong>Opt-Out:</strong> Users can text <strong>STOP</strong> to any of our SMS numbers at any time to cancel and stop receiving messages. You will receive a confirmation message acknowledging your opt-out request.
                </Typography>
                <Typography variant="body1" sx={{ color: '#fff', lineHeight: 1.8 }}>
                  <strong>Help:</strong> Users can text <strong>HELP</strong> to any of our SMS numbers to receive information about the service and support contact details.
                </Typography>
              </Box>
              <Typography variant="body1" sx={{ color: '#ccc', lineHeight: 1.8, mb: 2 }}>
                Additional SMS terms:
              </Typography>
              <ul style={{ color: '#ccc', lineHeight: 1.8, marginLeft: '1.5rem' }}>
                <li>Message and data rates may apply based on your mobile carrier's plan</li>
                <li>Message frequency varies based on business activity and customer interactions</li>
                <li>We support all major U.S. carriers</li>
                <li>We are not responsible for delayed or undelivered messages</li>
              </ul>
            </Box>

            <Box>
              <Typography variant="h5" gutterBottom sx={{ color: '#fff', fontWeight: 'bold', mb: 2 }}>
                5. Acceptable Use
              </Typography>
              <Typography variant="body1" sx={{ color: '#ccc', lineHeight: 1.8, mb: 2 }}>
                You agree not to use our services to:
              </Typography>
              <ul style={{ color: '#ccc', lineHeight: 1.8, marginLeft: '1.5rem' }}>
                <li>Violate any applicable laws or regulations</li>
                <li>Send spam, unsolicited messages, or engage in harassment</li>
                <li>Impersonate any person or entity</li>
                <li>Interfere with or disrupt the service or servers</li>
                <li>Attempt to gain unauthorized access to any systems</li>
                <li>Transmit viruses, malware, or other harmful code</li>
                <li>Collect or harvest information about other users</li>
              </ul>
            </Box>

            <Box>
              <Typography variant="h5" gutterBottom sx={{ color: '#fff', fontWeight: 'bold', mb: 2 }}>
                6. Payment and Billing
              </Typography>
              <Typography variant="body1" sx={{ color: '#ccc', lineHeight: 1.8, mb: 2 }}>
                For paid services:
              </Typography>
              <ul style={{ color: '#ccc', lineHeight: 1.8, marginLeft: '1.5rem' }}>
                <li>You agree to pay all fees associated with your selected plan</li>
                <li>Fees are billed in advance on a recurring basis</li>
                <li>All fees are non-refundable except as required by law</li>
                <li>We reserve the right to change our pricing with notice</li>
                <li>Failure to pay may result in service suspension or termination</li>
              </ul>
            </Box>

            <Box>
              <Typography variant="h5" gutterBottom sx={{ color: '#fff', fontWeight: 'bold', mb: 2 }}>
                7. Intellectual Property
              </Typography>
              <Typography variant="body1" sx={{ color: '#ccc', lineHeight: 1.8 }}>
                All content, features, and functionality of our services are owned by Reachout AI Corporation and are protected by copyright, trademark, and other intellectual property laws. You may not copy, modify, distribute, or create derivative works without our express written permission.
              </Typography>
            </Box>

            <Box>
              <Typography variant="h5" gutterBottom sx={{ color: '#fff', fontWeight: 'bold', mb: 2 }}>
                8. Data and Privacy
              </Typography>
              <Typography variant="body1" sx={{ color: '#ccc', lineHeight: 1.8 }}>
                Your use of our services is also governed by our Privacy Policy. By using our services, you consent to our collection and use of your data as described in the Privacy Policy.
              </Typography>
            </Box>

            <Box>
              <Typography variant="h5" gutterBottom sx={{ color: '#fff', fontWeight: 'bold', mb: 2 }}>
                9. Service Availability
              </Typography>
              <Typography variant="body1" sx={{ color: '#ccc', lineHeight: 1.8 }}>
                We strive to provide reliable service but do not guarantee uninterrupted access. We may modify, suspend, or discontinue any aspect of our services at any time without liability.
              </Typography>
            </Box>

            <Box>
              <Typography variant="h5" gutterBottom sx={{ color: '#fff', fontWeight: 'bold', mb: 2 }}>
                10. Limitation of Liability
              </Typography>
              <Typography variant="body1" sx={{ color: '#ccc', lineHeight: 1.8 }}>
                To the maximum extent permitted by law, Reachout AI Corporation shall not be liable for any indirect, incidental, special, consequential, or punitive damages resulting from your use or inability to use our services.
              </Typography>
            </Box>

            <Box>
              <Typography variant="h5" gutterBottom sx={{ color: '#fff', fontWeight: 'bold', mb: 2 }}>
                11. Indemnification
              </Typography>
              <Typography variant="body1" sx={{ color: '#ccc', lineHeight: 1.8 }}>
                You agree to indemnify and hold harmless Reachout AI Corporation from any claims, damages, losses, or expenses arising from your use of our services or violation of these terms.
              </Typography>
            </Box>

            <Box>
              <Typography variant="h5" gutterBottom sx={{ color: '#fff', fontWeight: 'bold', mb: 2 }}>
                12. Termination
              </Typography>
              <Typography variant="body1" sx={{ color: '#ccc', lineHeight: 1.8 }}>
                We reserve the right to suspend or terminate your account at any time for violation of these terms or for any other reason at our discretion. You may terminate your account at any time by contacting us.
              </Typography>
            </Box>

            <Box>
              <Typography variant="h5" gutterBottom sx={{ color: '#fff', fontWeight: 'bold', mb: 2 }}>
                13. Governing Law
              </Typography>
              <Typography variant="body1" sx={{ color: '#ccc', lineHeight: 1.8 }}>
                These Terms of Service shall be governed by and construed in accordance with the laws of the State of Colorado, without regard to its conflict of law provisions.
              </Typography>
            </Box>

            <Box>
              <Typography variant="h5" gutterBottom sx={{ color: '#fff', fontWeight: 'bold', mb: 2 }}>
                14. Changes to Terms
              </Typography>
              <Typography variant="body1" sx={{ color: '#ccc', lineHeight: 1.8 }}>
                We may modify these Terms of Service at any time. We will notify you of material changes by posting the updated terms on our website. Your continued use of our services after such changes constitutes acceptance of the new terms.
              </Typography>
            </Box>

            <Box>
              <Typography variant="h5" gutterBottom sx={{ color: '#fff', fontWeight: 'bold', mb: 2 }}>
                15. Contact Information
              </Typography>
              <Typography variant="body1" sx={{ color: '#ccc', lineHeight: 1.8 }}>
                If you have any questions about these Terms of Service, please contact us at:
              </Typography>
              <Box sx={{ mt: 2, p: 2, backgroundColor: 'rgba(0, 102, 255, 0.05)', borderRadius: '8px' }}>
                <Typography variant="body1" sx={{ color: '#fff', fontWeight: 'bold' }}>
                  Reachout AI Corporation
                </Typography>
                <Typography variant="body1" sx={{ color: '#ccc' }}>
                  1500 N GRANT ST # 7298
                </Typography>
                <Typography variant="body1" sx={{ color: '#ccc' }}>
                  DENVER, CO 80203
                </Typography>
                <Typography variant="body1" sx={{ color: '#0066FF', mt: 1 }}>
                  support@reachoutaiapp.com
                </Typography>
              </Box>
            </Box>
          </Box>
        </Paper>
      </Container>
    </motion.div>
  );
};

export default TermsOfService;
